<template>
  <div>
    <div>
      <p>
        Having trouble making sense of your merchant statements?  That's not an accident.  The major payment processors
        are wizards when it comes to hiding fees on your statements.  (But they're not wizards at much else.)  Entering your statement details here will help
        us locate areas where your processor is playing games.  We'll compare the interchange rates you're being charged
        against published interchange rates and look for other sneaky fees that shouldn't be there.
      </p>
      <p>
        We'll also use this information to update your effective interchange rate and improve the accuracy of your
        BlockChyp estimates.
      </p>
    </div>
    <div class="card">
      <h5 class="card-header">
        <span>Statement Summary</span>

        <!-- <a class="btn-recalc mg-l-5 float-right btn btn-sm btn-danger" @click="recalc()"><i class="fas fa-file-pdf"></i> UPLOAD PDF</a> -->
        <a class="btn-recalc mg-l-5 float-right btn btn-sm btn-danger" @click="recalc()"><i class="fas fa-sync"></i> RECALCULATE</a>
        <router-link :to="'/statement/' + statement.job.id" class="btn-recalc mg-l-5 float-right btn btn-sm btn-success"><i class="fas fa-arrow-left"></i> BACK TO COMPARISON</router-link>
      </h5>
      <div class="card-body">
        <div class="row">
          <div class="col-md-4">
            <div class="form-group">
              <label class="form-control-label">Processor:</label>
              <input class="form-control" v-model="statement.processor.name" @blur="recalc()"/>
              <div class="text-muted">This is the name of the processor.</div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group">
              <label class="form-control-label">Expected Volume:</label>
              <currency-input v-model="statement.job.volume" @blur="recalc()"/>
              <div class="text-muted">This is what you entered in the comparison tool for monthly volume.  We'll compare it to what you enter below and make sure they line up.</div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="form-group">
              <label class="form-control-label">Expected Transaction Count:</label>
              <input class="form-control" v-model="statement.job.transactionCount" @blur="recalc()"/>
              <div class="text-muted">This is what you entered in the comparison tool for transaction count.  We'll compare it to what you enter below and make sure they line up.</div>
            </div> <!-- row -->
          </div> <!-- col -->
        </div> <!-- row -->
      </div>
    </div>
    <div class="card mg-t-20" v-if="summaryVisible">
      <div class="card-body">
        <div class="row">
          <div class="col-md-3 text-center">
            <div v-if="statement.processor.summary.effectiveInterchange">
              <div>Interchange Entered: ${{statement.processor.summary.totalInterchange}}</div>
              <div class="ratePct" v-if="statement.processor.summary.effectiveInterchange">{{statement.processor.summary.effectiveInterchange}}</div>
              <div class="rateDesc" v-if="statement.processor.summary.effectiveInterchange">Effective Interchange</div>
              <div v-if="statement.processor.expectedInterchange"><span class="badge badge-warning">Published Rate: {{statement.processor.expectedInterchange}}%</span></div>
            </div>
          </div>
          <div class="col-md-3 text-center">
            <div v-if="statement.processor.summary.effectiveAssessments">
              <div>Assessments Entered: ${{statement.processor.summary.totalAssessments}}</div>
              <div class="ratePct" v-if="statement.processor.summary.effectiveAssessments">{{statement.processor.summary.effectiveAssessments}}</div>
              <div class="rateDesc" v-if="statement.processor.summary.effectiveAssessments">Effective Assessments</div>
              <div v-if="statement.processor.expectedAssessments"><span class="badge badge-warning">Published Rate: {{statement.processor.expectedAssessments}}%</span></div>
            </div>
          </div>
          <div class="col-md-3 text-center">
            <div>Total Fees Entered: ${{statement.processor.summary.totalFees}}</div>
            <div class="ratePct" v-if="statement.processor.summary.effectiveRate">{{statement.processor.summary.effectiveRate}}</div>
            <div class="rateDesc" v-if="statement.processor.summary.effectiveRate">Effective Rate</div>
          </div>
          <div class="col-md-3 text-center">
            <div class="font-weight-bold">Current Processor Charge</div>
            <div class="rateDesc" v-if="statement.processor.summary.interchangePlusRate">interchange and assessments</div>
            <div class="ratePct" v-if="statement.processor.summary.interchangePlusRate">+{{statement.processor.summary.interchangePlusRate}}</div>
            <div class="rateDesc font-weight-bold" v-if="statement.processor.summary.interchangePlusAmount">(+${{statement.processor.summary.interchangePlusAmount}})</div>
          </div>
        </div>
      </div>
      <div class="text-muted text-center">
        Volume Entered: ${{statement.processor.summary.interchangeVolume}} / ${{statement.processor.summary.expectedVolume}}
      </div>
      <div class="progress">
        <div class="progress-bar bg-info"
          role="progressbar"
          :style="'width: '  + statement.processor.summary.progress + '%;'"
          :aria-valuenow="statement.processor.summary.progress"
          aria-valuemin="0"
          aria-valuemax="100">{{statement.processor.summary.progress}}%
        </div>
      </div>

    </div>
    <div class="card mg-t-20">
      <div class="card-body">
       <nav>
        <div class="nav nav-tabs" id="nav-tab" role="tablist">
          <a class="nav-item nav-link active" id="nav-enter-tab" data-toggle="tab" href="#nav-enter" role="tab" aria-controls="nav-home" aria-selected="true">Enter Data</a>
          <a class="nav-item nav-link" id="nav-interchange-tab" data-toggle="tab" href="#nav-interchange" role="tab" aria-controls="nav-interchange" aria-selected="false">Interchange <span class="badge badge-success" v-if="statement.processor.interchangeLines.actualTotal">${{statement.processor.interchangeLines.actualTotal}}</span></a>
          <a class="nav-item nav-link" id="nav-assessments-tab" data-toggle="tab" href="#nav-assessments" role="tab" aria-controls="nav-assessments" aria-selected="false">Assessments <span class="badge badge-success" v-if="statement.processor.assessmentLines.actualTotal">${{statement.processor.assessmentLines.actualTotal}}</span></a>
          <a class="nav-item nav-link" id="nav-monthly-tab" data-toggle="tab" href="#nav-monthly" role="tab" aria-controls="nav-monthly" aria-selected="false">Monthly Fees <span class="badge badge-success" v-if="statement.processor.monthlyLines.actualTotal">${{statement.processor.monthlyLines.actualTotal}}</span></a>
          <a class="nav-item nav-link" id="nav-other-tab" data-toggle="tab" href="#nav-other" role="tab" aria-controls="nav-other" aria-selected="false">Other Nonsense <span class="badge badge-success" v-if="statement.processor.otherLines.actualTotal">${{statement.processor.otherLines.actualTotal}}</span></a>
        </div>
       </nav>
      <div class="tab-content" id="nav-tabContent">
          <div class="tab-pane fade show active" id="nav-enter" role="tabpanel" aria-labelledby="nav-enter-tab">
            <enter-line-item :categories="categories" @addLineItem="handleNewLine"/>
          </div>
          <div class="tab-pane fade" id="nav-interchange" role="tabpanel" aria-labelledby="nav-interchange-tab">
            <line-item-table  @updateLineItem="handleLineItemUpdate" @deleteLineItem="handleLineItemDelete" :lineItems="statement.processor.interchangeLines"/>
            <enter-line-item caption="Add More Data" :categories="categories" @addLineItem="handleNewLine"/>
          </div>
          <div class="tab-pane fade" id="nav-assessments" role="tabpanel" aria-labelledby="nav-assessments-tab">
            <line-item-table @updateLineItem="handleLineItemUpdate" @deleteLineItem="handleLineItemDelete" :lineItems="statement.processor.assessmentLines"/>
            <enter-line-item caption="Add More Data" :categories="categories" @addLineItem="handleNewLine"/>
          </div>
          <div class="tab-pane fade" id="nav-monthly" role="tabpanel" aria-labelledby="nav-monthly-tab">
            <line-item-table @updateLineItem="handleLineItemUpdate" @deleteLineItem="handleLineItemDelete" :lineItems="statement.processor.monthlyLines"/>
            <enter-line-item caption="Add More Data" :categories="categories" @addLineItem="handleNewLine"/>
          </div>
          <div class="tab-pane fade" id="nav-other" role="tabpanel" aria-labelledby="nav-other-tab">
            <line-item-table @updateLineItem="handleLineItemUpdate" @deleteLineItem="handleLineItemDelete" :lineItems="statement.processor.otherLines"/>
            <enter-line-item caption="Add More Data" :categories="categories" @addLineItem="handleNewLine"/>
          </div>
      </div>
    </div>
    </div>
    <!-- /.content -->
    <delete-modal
       title="Confirm Delete"
       v-on:deleteConfirmed="deleteConfirmed"
       id="modal-delete"
       :returnId="deleteLineItem.id"
       message="Are you sure you want to delete this line item?">
   </delete-modal>
  </div>
</template>

<script>
import axios from 'axios'
import CurrencyInput from '@blockchyp/crud-magic/src/widgets/CurrencyInput'
import PercentInput from '@/components/PercentInput'
import EnterStatementLineItem from '@/components/EnterStatementLineItem'
import LineItemTable from '@/components/LineItemTable'
import DeleteModal from '@blockchyp/crud-magic/src/modals/DeleteModal'
import modalService from '@blockchyp/crud-magic/src/services/ModalService'

export default {
  name: 'statement-details',
  props: ['id'],
  components: {
    'currency-input': CurrencyInput,
    'percent-input': PercentInput,
    'enter-line-item': EnterStatementLineItem,
    'line-item-table': LineItemTable,
    'delete-modal': DeleteModal
  },
  data: () => ({
    categories: [],
    deleteLineItem: {id: null},
    statement: {
      job: {},
      processor: {
        interchangeLines: {
          lineItems: []
        },
        assessmentLines: {
          lineItems: []
        },
        monthlyLines: {
          lineItems: []
        },
        otherLines: {
          lineItems: []
        },
      }
    }
  }),
  computed: {
    summaryVisible: function () {

      if (this.statement && this.statement.processor && this.statement.processor.summary) {
        if (this.statement.processor.summary.progress > 0) {
          return true
        }

        if (this.statement.processor.summary.totalFees) {
          return true
        }
      }

      return false

    }
  },
  methods: {
    handleLineItemDelete: function (line) {
      this.deleteLineItem = line
      modalService.raiseModal('modal-delete')
    },
    deleteConfirmed: function (deleteId) {
      let self = this
      axios.delete('/api/analyze-line-item/' + deleteId)
        .then(function (response) {
          self.statement = response.data
        })
        .catch(function (error) {
          console.log(error)
        })
    },
    handleNewLine: function (line) {
      let self = this
      line.currentProcessorId = this.statement.processor.id
      axios.post('/api/analyze-line-item', line)
        .then(function (response) {
          self.statement = response.data
        })
        .catch(function (error) {
          console.log(error)
        })
    },
    handleLineItemUpdate: function (line) {
      let self = this
      axios.post('/api/analyze-line-item', line)
        .then(function (response) {
          self.statement = response.data
        })
        .catch(function (error) {
          console.log(error)
        })
    },
    recalc: function () {
      let self = this
      axios.post('/api/analyze-statement', self.statement)
        .then(function (response) {
          self.statement = response.data
        })
        .catch(function (error) {
          console.log(error)
        })
    }
  },
  mounted () {

    this.$emit("captionChange", "STATEMENT ANALYSIS TOOL")
    let self = this
    let url = '/api/analyze-statement'
    if (self.id) {
      url = url + "?id=" + self.id
    } else {
      url = url + "?jobId=" + self.$route.query.jobId
    }
    axios.get(url)
      .then(function (response) {
        self.statement = response.data
      })
      .catch(function (error) {
        console.log(error)
      })

    axios.get('/api/analyze-fee-categories')
      .then(function (response) {
        self.categories = response.data.results
      })
      .catch(function (error) {
        console.log(error)
      })

  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>

.btn-recalc {
  color: white !important;
}

.shareBlock {
  border-top: solid 1px black;
  border-bottom: solid 1px black;
  margin-left: 0 !important;
}

.shareBlock .btn {
    color: white !important;
}

.linkBox {
  min-width: 500px;
  margin-right: auto;
}

.effRate {
  font-weight: bold;
  font-size: 15pt;
}

.ratePct {
  font-weight: bold;
  font-size: 20pt;
}

.rateDesc {
  font-size: 8pt;
}

#inviteCode {
  max-width: 150px;
}

</style>
